/**
 * The event's seat limit: `null` means unlimited, a missing prop (entries
 * saved before the registration settings existed) means the legacy default
 * of 30. One definition for client and server — EventEntryModel's constructor
 * default of 30 matches the missing-prop case.
 */
export function get_event_max_attendees(entry) {
  if (entry?.max_attendees === null) return Infinity;
  return Number.isInteger(entry?.max_attendees) ? entry.max_attendees : 30;
}

/** Cancelled or postponed events accept no new registrations or promotions */
export function is_event_registration_restricted(entry) {
  return entry?.event_status === 'cancelled' || entry?.event_status === 'postponed';
}

/**
 * Seat arithmetic shared by the event ticket, the registration form, the
 * registration card, the guest list and the server's registration endpoints:
 * `max_attendees` null means unlimited seats, pending rows are unpaid seat
 * holds and take a seat like confirmed ones, and paid events have no waitlist.
 *
 * @param {object} entry - EventEntryModel (or its preview, or server props)
 * @param {object} [data] - additional_entry_data from the server
 */
export function get_event_capacity(entry, data = {}) {
  const max_attendees = get_event_max_attendees(entry);
  const is_unlimited = max_attendees === Infinity;
  const seats_taken = (data.confirmed_count || 0) + (data.pending_count || 0);
  const waitlist_capacity = entry?.payment_required ? 0 : (entry?.waitlist_capacity || 0);
  const waitlist_count = data.waitlist_count || 0;
  const remaining_seats = is_unlimited ? Infinity : Math.max(0, max_attendees - seats_taken);
  const remaining_waitlist = Math.max(0, waitlist_capacity - waitlist_count);
  const is_full = remaining_seats === 0;

  return {
    max_attendees,
    is_unlimited,
    seats_taken,
    remaining_seats,
    waitlist_capacity,
    waitlist_count,
    remaining_waitlist,
    is_full,
    has_waitlist_space: is_full && remaining_waitlist > 0,
    // Few seats left: an absolute or relative threshold, whichever hits first
    is_low: !is_unlimited && remaining_seats > 0
      && (remaining_seats <= 5 || remaining_seats / max_attendees <= 0.2),
    // Seats (incl. waitlist spots) that can still be handed out right now
    available: is_unlimited ? Infinity : remaining_seats + remaining_waitlist,
  };
}

/** Whether the event's end (or start, for open-ended events) lies in the past */
export function has_event_concluded(entry) {
  const end = entry?.ends_at || entry?.starts_at;
  if (!end) return false;
  return new Date(end).getTime() < Date.now();
}
